import JSZip from "jszip";
import { detectDownloadSupport } from "@/lib/utils/browserDetect";

/**
 * Result of a download attempt, letting callers choose the right UX
 * (toast, long-press hint, etc.) instead of relying on console output.
 */
export type DownloadOutcome =
  | { status: "shared" } // share sheet completed
  | { status: "dismissed" } // user closed share sheet (NOT a failure)
  | { status: "downloaded" } // blob <a download> clicked
  | { status: "opened" } // window.open fallback succeeded (real browsers)
  | { status: "hint" } // restricted WebView — caller shows long-press hint
  | { status: "error"; error: Error };

/**
 * Downloads a single file from a URL using a capability cascade:
 * restricted-UA hint → Web Share → blob <a download> → window.open.
 * @param url - The URL of the file to download
 * @param filename - The desired filename for the download
 * @param mimeType - Optional fallback MIME type when the response omits one
 */
export const downloadFile = async (
  url: string,
  filename: string,
  mimeType?: string
): Promise<DownloadOutcome> => {
  if (detectDownloadSupport(navigator.userAgent).isDownloadRestricted) {
    return { status: "hint" };
  }

  try {
    const response = await fetch(url);
    if (!response.ok) {
      // An expired signed URL resolves (does not reject) with an error
      // status and a JSON body, so treat it as a failure to reach the
      // window.open fallback instead of saving a corrupt file.
      throw new Error(`Failed to fetch file: HTTP ${response.status}`);
    }
    const blob = await response.blob();

    const file = new File([blob], filename, {
      type: blob.type || mimeType || "application/octet-stream",
    });

    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: "Your KeptScene photo" });
        return { status: "shared" };
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return { status: "dismissed" };
        }
        // Any other share failure falls through to the blob download path.
      }
    }

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the blob URL after a short delay
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);

    return { status: "downloaded" };
  } catch (error) {
    // Fallback: open in new tab if download fails
    const opened = window.open(url, "_blank");
    if (opened) {
      return { status: "opened" };
    }
    return {
      status: "error",
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
};

/**
 * Downloads multiple files as a single ZIP archive
 * @param files - Array of objects containing url and filename
 * @param zipFilename - The desired filename for the ZIP archive
 * @throws Will fall back to individual downloads if ZIP creation fails
 */
export const downloadAsZip = async (
  files: Array<{ url: string; filename: string }>,
  zipFilename: string = "download.zip"
): Promise<void> => {
  try {
    const zip = new JSZip();
    
    // Fetch all files and add to ZIP
    const fetchPromises = files.map(async ({ url, filename }) => {
      const response = await fetch(url);
      const blob = await response.blob();
      zip.file(filename, blob);
    });
    
    await Promise.all(fetchPromises);
    
    // Generate ZIP file
    const zipBlob = await zip.generateAsync({ type: "blob" });
    
    // Download the ZIP file
    const blobUrl = window.URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = zipFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the blob URL after a short delay
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
  } catch (error) {
    console.error('ZIP Download failed:', error);
    // Fallback to individual downloads
    for (const { url, filename } of files) {
      await downloadFile(url, filename);
      // Add delay between downloads to avoid browser blocking
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }
};

/**
 * Downloads photo booth results (image and optional video)
 * Creates a ZIP if multiple files exist, otherwise downloads single file
 * @param imagePath - URL of the image file
 * @param videoPath - Optional URL of the video file
 */
export const downloadPhotoResult = async (
  imagePath: string,
  videoPath?: string
): Promise<void> => {
  if (!imagePath) {
    console.error('No image path provided');
    return;
  }

  // If both files exist, create a ZIP
  if (imagePath && videoPath) {
    await downloadAsZip(
      [
        { url: imagePath, filename: "result.jpg" },
        { url: videoPath, filename: "result.mp4" }
      ],
      "keptscene-result.zip"
    );
  } else {
    // Download single image
    await downloadFile(imagePath, "result.jpg");
  }
};

